import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { EnvelopeIcon, MagnifyingGlassIcon } from '@phosphor-icons/react'
import { useTheme } from 'styled-components'

import { Button, ButtonWithSpinner, EmailInput, Text } from '@reveni-io/reveni-ui'

import ActionsContainer from '../components/ActionsContainer'
import PortalLayout from '../components/PortalLayout/PortalLayout'
import { inputMaxLengths } from '../constants'
import AppContext from '../context/AppContext'
import { getPortalBasePath } from '../helpers/customDomain/customDomain'
import useClearCustomerToken from '../hooks/useClearCustomerToken/useClearCustomerToken'
import usePortalPage from '../hooks/usePortalPage/usePortalPage'
import CheckYourEmailModal from './CheckYourEmailModal/CheckYourEmailModal'
import useOrderRecoveryLink from './hooks/useOrderRecoveryLink/useOrderRecoveryLink'
import * as Styles from './styles'

const FindOrdersPage = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const theme = useTheme()
  const { context } = useContext(AppContext)
  const { loadingReturnPortalInfo, changeLanguage, descriptionText } = usePortalPage({
    slug,
    requiresOrderRecovery: true,
  })
  const {
    email,
    isEmailValid,
    handleEmailChange,
    sendRecoveryLink,
    loading,
    showCheckYourEmailModal,
    closeCheckYourEmailModal,
    secondsToResend,
  } = useOrderRecoveryLink({ portalId: context.merchantInfo.returnPortalId })

  useClearCustomerToken()

  const handleSubmit = e => {
    e.preventDefault()
    sendRecoveryLink()
  }

  const portalBasePath = getPortalBasePath(slug)

  const handleCancel = () => navigate(portalBasePath || '/')

  return (
    <PortalLayout
      loading={loadingReturnPortalInfo}
      descriptionText={descriptionText}
      onChangeLanguage={changeLanguage}
      headerAboveCard
    >
      <Styles.Container>
        <Styles.Heading>
          <MagnifyingGlassIcon size={40} color={theme.title.primary} />
          <Text type="titleM">{t('Encuentra tus pedidos recientes')}</Text>
        </Styles.Heading>
        <Text type="bodyMRegular">
          {t('Introduce el email con el que hiciste el pedido — te enviaremos un enlace seguro.')}
        </Text>
        <Styles.Form onSubmit={handleSubmit}>
          <EmailInput
            Icon={EnvelopeIcon}
            label={t('Email')}
            placeholder="email@example.com"
            value={email}
            onChange={handleEmailChange}
            name="recoveryEmail"
            maxLength={inputMaxLengths.orderEmail}
          />
          <ActionsContainer>
            <ButtonWithSpinner
              type="submit"
              fullWidth
              disabled={!isEmailValid || loading}
              loading={loading}
              defaultLabel={t('Enviar enlace')}
              loadingLabel={t('Enviando')}
            />
            <Button type="button" variant="text" fullWidth onClick={handleCancel}>
              {t('Cancelar')}
            </Button>
          </ActionsContainer>
        </Styles.Form>
      </Styles.Container>
      <CheckYourEmailModal
        show={showCheckYourEmailModal}
        email={email}
        merchantName={context.merchantInfo.name}
        secondsToResend={secondsToResend}
        loading={loading}
        onResend={sendRecoveryLink}
        onUseDifferentEmail={closeCheckYourEmailModal}
      />
    </PortalLayout>
  )
}

export default FindOrdersPage
